import React, { useEffect, useState } from 'react'
import {
	Box,
	Grid,
	Paper,
	Skeleton,
	Typography,
} from '@mui/material'

import { dsColorNew, dataColors } from '@palette'
import { MB_CHART_COLORS } from 'Pages/MyBusiness/_shared/Ui'
import { AppEmptyLine, AppNote } from '@components'

// Same colour per bucket as the Revenue Sources table
const BUCKET_COLORS = {
	tarifa_base:         dsColorNew.webBlue500,
	cargos_operativos:   dsColorNew.tableHead,
	ingresos_auxiliares: dsColorNew.cyan,
	recuperacion_costes: dataColors.violet500,
}

/**
 * Sprint 2 · point 3 of Thibault's mail — 4 KPI cards on top of Revenue.
 *
 *   Tarifa Base · Cargos Operativos · Ingresos Auxiliares · Recuperación Costes
 *
 * Consumes /api/my-business/revenue/by-bucket. Extras Buckets admin
 * seeds those 4 by default; the operator can rename or add more from
 * Admin → Extras Buckets and the widget auto-adapts.
 *
 * If a booking item's service_type is not mapped to a bucket, the
 * revenue lands in a synthetic 'Unassigned' card so the operator sees
 * the gap.
 */
const fmtMoney = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
	}).format(n)
const fmtPct = (n) =>
	new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
		.format(n) + '%'

export default function RevenueBucketsBanner({
	from, to, offices = [], categories = [],
}) {
	const [data, setData]       = useState(null)
	const [error, setError]     = useState(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		if (!from || !to) return
		setLoading(true); setError(null)
		const qs = new URLSearchParams({ from, to })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		fetch(`/api/my-business/revenue/by-bucket?${qs}`, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [from, to, offices.join(','), categories.join(',')])

	if (error) return (
		<AppNote severity="error" sx={{ mb: 2 }}>
			Revenue-by-bucket unreachable: {error}
		</AppNote>
	)
	if (loading || !data) return (
		<Skeleton variant="rounded" height={92} sx={{ borderRadius: '12px' }}/>
	)
	const buckets = data.buckets || []
	if (!buckets.length) return (
		<Paper sx={{ p: 2 }}>
			<AppEmptyLine>No buckets defined yet. Configure them in <strong>Admin → Extras Buckets</strong>.</AppEmptyLine>
		</Paper>
	)
	return (
		<Grid container spacing={2}>
			{buckets.map((b, i) => (
				<Grid item key={b.code} xs={12} sm={6} md={12 / Math.max(buckets.length, 3)}>
					<BucketCard bucket={b} color={BUCKET_COLORS[b.code] || MB_CHART_COLORS[i % MB_CHART_COLORS.length]}/>
				</Grid>
			))}
		</Grid>
	)
}

function BucketCard({ bucket, color }) {
	const isUnassigned = bucket.code === 'unassigned'
	return (
		<Paper sx={{
			px: 2, py: 1.5, height: '100%',
			display: 'flex', flexDirection: 'column',
			...(isUnassigned && { bgcolor: dsColorNew.noteAmberBg, borderColor: `${dsColorNew.noteAmberBorder} !important` }),
		}}>
			<Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 11, fontWeight: 600, letterSpacing: 1,
			                  textTransform: 'uppercase', color: isUnassigned ? dsColorNew.noteAmber : dsColorNew.textMuted }}>
				<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
				                             bgcolor: isUnassigned ? dsColorNew.warningBar : color }}/>
				{bucket.name}
			</Typography>
			<Typography sx={{ fontSize: 19, fontWeight: 700, color: dsColorNew.textTitle, mt: 0.5 }}>
				{fmtMoney(bucket.revenue)}
			</Typography>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				{fmtPct(bucket.share_pct)} share
			</Typography>
		</Paper>
	)
}
